import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';

export interface JukeboxCliente {
  id: string;
  phone_e164: string;
  nome: string | null;
  criado_em: string;
  avatar_url?: string | null;
  whatsapp_push_name?: string | null;
  /** Segredo emitido no login OTP — prova a posse do cliente nas RPCs (cliente_id e session_id são públicos). */
  token: string;
}

export interface JukeboxClienteContextValue {
  status: 'anonymous' | 'authenticated';
  cliente: JukeboxCliente | null;
  sessionId: string;
  openLogin: () => void;
  logout: () => void;
  isLoginOpen: boolean;
  closeLogin: () => void;
  onLoginSuccess: (cliente: JukeboxCliente) => void;
  /** Carimbo de `musica_atual` / `pedido_iniciado`. O layout não desmonta entre abas. */
  ultimaAtividadePlayer: number | null;
  registrarAtividadePlayer: () => void;
}

const JukeboxClienteContext = createContext<JukeboxClienteContextValue | null>(null);

import { obterOuCriarSessionId } from '@/shared/lib/jukeboxSession';

const CLIENTE_KEY = (telaId: string) => `ds:jukebox_cliente:${telaId}`;

function loadCliente(telaId: string): JukeboxCliente | null {
  try {
    const raw = localStorage.getItem(CLIENTE_KEY(telaId));
    if (!raw) return null;
    const cliente = JSON.parse(raw) as Partial<JukeboxCliente>;
    // Login salvo antes do token existir não autentica mais — pede novo OTP.
    if (!cliente.id || !cliente.token) return null;
    return cliente as JukeboxCliente;
  } catch {
    return null;
  }
}

interface Props {
  telaId: string;
  children: ReactNode;
}

export function JukeboxClienteProvider({ telaId, children }: Props) {
  const [cliente, setCliente] = useState<JukeboxCliente | null>(() => loadCliente(telaId));
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [sessionId] = useState(() => obterOuCriarSessionId(telaId));
  const [ultimaAtividadePlayer, setUltimaAtividadePlayer] = useState<number | null>(null);
  const registrarAtividadePlayer = useCallback(() => {
    setUltimaAtividadePlayer(Date.now());
  }, []);

  useEffect(() => {
    const stored = loadCliente(telaId);
    if (stored) setCliente(stored);
  }, [telaId]);

  const openLogin = useCallback(() => setIsLoginOpen(true), []);
  const closeLogin = useCallback(() => setIsLoginOpen(false), []);

  const onLoginSuccess = useCallback((c: JukeboxCliente) => {
    setCliente(c);
    localStorage.setItem(CLIENTE_KEY(telaId), JSON.stringify(c));
    setIsLoginOpen(false);
  }, [telaId]);

  const logout = useCallback(() => {
    setCliente(null);
    localStorage.removeItem(CLIENTE_KEY(telaId));
  }, [telaId]);

  const value: JukeboxClienteContextValue = {
    status: cliente ? 'authenticated' : 'anonymous',
    cliente,
    sessionId,
    openLogin,
    logout,
    isLoginOpen,
    closeLogin,
    onLoginSuccess,
    ultimaAtividadePlayer,
    registrarAtividadePlayer,
  };

  return (
    <JukeboxClienteContext.Provider value={value}>
      {children}
    </JukeboxClienteContext.Provider>
  );
}

export function useJukeboxCliente(): JukeboxClienteContextValue {
  const ctx = useContext(JukeboxClienteContext);
  if (!ctx) throw new Error('useJukeboxCliente must be used inside JukeboxClienteProvider');
  return ctx;
}
